import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Zap, ShieldAlert, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRM_PHRASE = "SYNC WAITLIST";

export default function WaitlistSyncPanel({ token, onClose }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/waitlist/ghl-sync/preview`, H)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the dry-run report."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const runBackfill = async () => {
    if (phrase.trim() !== CONFIRM_PHRASE) { toast.error(`Type the exact phrase "${CONFIRM_PHRASE}" to run the backfill.`); return; }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/waitlist/ghl-sync/backfill`, { confirm: phrase.trim() }, H);
      setResult(data);
      toast.success(`Backfill done — ${data.synced} synced, ${data.skipped} already synced/skipped, ${data.failed.length} failed.`);
      setConfirmOpen(false); setPhrase(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed."); }
    finally { setRunning(false); }
  };

  const t = report?.totals || {};
  const stat = (key, label, val, cls = "text-white") => (
    <div key={key} data-testid={`ws-total-${key}`} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className={`text-lg font-bold tabular-nums ${cls}`}>{val ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wide text-white/45">{label}</div>
    </div>
  );

  return (
    <div data-testid="waitlist-sync-panel" className="bg-[#0C0C0C] border border-sky-500/25 rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-white font-bold text-sm flex items-center gap-2"><Zap size={14} className="text-sky-300" /> Waitlist → GoHighLevel Sync</h4>
        <span data-testid="ws-dryrun-badge" className="text-[9px] font-bold text-sky-300 border border-sky-500/40 rounded px-1.5 py-0.5">DRY-RUN PREVIEW — NOTHING WRITTEN</span>
        <span className="text-white/35 text-[10px]">tags: <b className="text-white/60">waitlist-active</b> / <b className="text-white/60">waitlist-closed</b></span>
        <button data-testid="ws-refresh" onClick={load} className="ml-auto text-white/50 hover:text-white" title="Refresh the dry-run report"><RefreshCw size={13} /></button>
        <button data-testid="ws-close" onClick={onClose} className="text-white/50 hover:text-white"><X size={14} /></button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm py-4 justify-center"><Loader2 className="animate-spin" size={15} /> Running dry-run…</div>
      ) : !report ? (
        <div className="text-red-300 text-xs">Could not load the report.</div>
      ) : (
        <>
          {!report.config_ok && (
            <div data-testid="ws-config-problems" className="rounded-lg border border-red-500/30 bg-red-500/[0.06] px-3 py-2 text-xs text-red-300 space-y-0.5">
              <div className="font-bold flex items-center gap-1.5"><ShieldAlert size={12} /> GHL is not configured — backfill is blocked:</div>
              {report.config_problems.map((p, i) => <div key={i}>• {p}</div>)}
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {stat("records", "Waitlist Records", t.waitlist_records)}
            {stat("eligible", "Eligible", t.eligible, "text-emerald-300")}
            {stat("missing-phone", "Missing / Bad Phone", t.missing_or_invalid_phone, t.missing_or_invalid_phone ? "text-amber-300" : "text-white")}
            {stat("duplicates", "Duplicates", t.duplicates, t.duplicates ? "text-amber-300" : "text-white")}
            {stat("already", "Already Synced", t.already_synced, "text-sky-300")}
            {stat("would-change", "Would Change", t.would_change, "text-[#F2B705]")}
          </div>

          {(report.duplicates || []).length > 0 && (
            <div data-testid="ws-duplicates" className="text-[11px] text-amber-300/90 space-y-0.5">
              {report.duplicates.map((d, i) => (
                <div key={i}>⚠ Duplicate contact <b>{d.key}</b>: {d.records.map((x) => x.name).join(" + ")} — GHL will merge these into one contact.</div>
              ))}
            </div>
          )}

          {(report.records || []).length > 0 && (
            <div data-testid="ws-records" className="max-h-56 overflow-y-auto space-y-1 pr-1">
              {report.records.map((r) => (
                <div key={r.id} data-testid={`ws-record-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded border border-white/8 bg-white/[0.02] px-2.5 py-1.5 text-[11px]">
                  <span className="text-white/75"><b>{r.name || "—"}</b> · {r.phone || <span className="text-amber-300">no valid phone</span>}{r.email ? ` · ${r.email}` : ""} <span className="text-white/35">· waiting since {r.waiting_since}</span></span>
                  <span className={r.already_synced ? "text-sky-300" : r.eligible ? "text-emerald-300" : "text-amber-300"}>{r.proposed_action}{r.sync_error ? ` · last error: ${r.sync_error}` : ""}</span>
                </div>
              ))}
            </div>
          )}
          {t.waitlist_records === 0 && <div className="text-white/40 text-xs">No one is on the waitlist right now — new waitlist entries sync to GoHighLevel automatically.</div>}

          {result && (
            <div data-testid="ws-results" className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.05] px-3 py-2 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle2 size={13} /> Backfill ran: {result.synced} synced · {result.skipped} already synced/skipped · {result.failed.length} failed{result.failed.length > 0 ? ` — ${result.failed.map((f) => f.error).join("; ").slice(0, 200)}` : ""}
            </div>
          )}

          {!confirmOpen ? (
            <button data-testid="ws-backfill-btn" onClick={() => setConfirmOpen(true)}
              disabled={!report.config_ok || t.waitlist_records === 0}
              className="px-4 py-2 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 text-xs font-bold hover:bg-sky-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Run Live Backfill…
            </button>
          ) : (
            <div data-testid="ws-confirm-box" className="rounded-lg border border-[#F2B705]/40 bg-[#F2B705]/[0.05] p-3 space-y-2">
              <div className="text-xs text-white/80">
                This will write to GoHighLevel: upsert <b>{t.eligible}</b> contact(s) and apply the <b>waitlist-active</b> tag ({t.already_synced} already synced, {t.would_skip} skipped for missing contact info). No SMS is sent by AutoHire — GoHighLevel owns all messages.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="ws-confirm-input" value={phrase} onChange={(e) => setPhrase(e.target.value)}
                  placeholder={`Type ${CONFIRM_PHRASE} to confirm`}
                  className="bg-black border border-white/20 rounded-md px-3 py-1.5 text-xs text-white w-56" />
                <button data-testid="ws-run-backfill" onClick={runBackfill} disabled={running || phrase.trim() !== CONFIRM_PHRASE}
                  className="px-4 py-1.5 rounded-md bg-[#F2B705] text-black text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                  {running && <Loader2 className="animate-spin" size={12} />} Run Backfill Now
                </button>
                <button data-testid="ws-cancel-backfill" onClick={() => { setConfirmOpen(false); setPhrase(""); }} className="text-white/50 text-xs hover:text-white">Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
